import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn } from "@/components/mnf/UI";
import { useStoreQuery, queries, mutations, type SavedWorkout } from "@/lib/store";

/**
 * /move/library
 * The sister's bookmarked workouts. Empty state encourages saving from /move.
 */
export default function Library() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: LibraryRow below WRITES (toggleSaveWorkout,
  // used to unsave). See the early return just below.
  const userId = me?.id ?? null;
  const saved = useStoreQuery(() => (userId ? queries.savedWorkoutsForUser(userId) : []));

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see your saved workouts, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="cream"
        eyebrow="Your library"
        title={
          <>
            Saved <span className="italic">workouts</span>
          </>
        }
        subtitle={<>A library of movements you'll return to, sister.</>}
        trailing={
          <Link href="/move">
            <span
              className="inline-flex items-center justify-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: "rgba(30,74,53,0.08)",
                color: "var(--mnf-fd)",
                cursor: "pointer",
              }}
              aria-label="Back to today's workout"
            >
              <Icon name="arrow_back" size={18} />
            </span>
          </Link>
        }
      />

      <div className="px-5 pb-6 space-y-3">
        {saved.length === 0 ? (
          <GlassCard organic padding={26}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
              EMPTY FOR NOW
            </p>
            <h3
              className="mnf-serif"
              style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2, marginTop: 8, color: "var(--mnf-fd)" }}
            >
              Your library is quiet for now.
            </h3>
            <p
              className="mnf-body"
              style={{ fontSize: 13, marginTop: 10, color: "var(--mnf-tg)", lineHeight: 1.55 }}
            >
              When a workout fits you well, tap the bookmark and it will live here for the next time you need it.
            </p>
            <div style={{ marginTop: 16 }}>
              <Link href="/move">
                <Btn variant="primary" size="md">
                  <Icon name="self_improvement" size={16} />
                  <span style={{ marginLeft: 6 }}>Begin today's workout</span>
                </Btn>
              </Link>
            </div>
          </GlassCard>
        ) : (
          saved.map((w) => <LibraryRow key={w.id} workout={w} userId={userId} />)
        )}

        {saved.length > 0 && (
          <p
            className="mnf-body text-center"
            style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 10, opacity: 0.8 }}
          >
            {saved.length} {saved.length === 1 ? "workout" : "workouts"} saved
          </p>
        )}
      </div>
    </>
  );
}

function LibraryRow({ workout, userId }: { workout: SavedWorkout; userId: string }) {
  function unsave() {
    mutations.toggleSaveWorkout({
      userId,
      workoutId: workout.workoutId,
      title: workout.title,
      focus: workout.focus,
      durationMin: workout.durationMin,
      movements: workout.movements,
    });
  }

  return (
    <GlassCard organic padding={20}>
      <div className="flex items-start justify-between gap-3">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", fontSize: 10 }}>
            {workout.focus.toUpperCase()}
          </p>
          <h3
            className="mnf-serif"
            style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.2, marginTop: 6, color: "var(--mnf-fd)" }}
          >
            {workout.title}
          </h3>
          <p className="mnf-body" style={{ fontSize: 12, marginTop: 6, color: "var(--mnf-tg)" }}>
            {workout.durationMin} min · {workout.movements} movements
          </p>
        </div>
        <button
          type="button"
          onClick={unsave}
          aria-label={`Remove ${workout.title} from your library`}
          className="inline-flex items-center justify-center"
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: "rgba(192,120,74,0.10)",
            color: "var(--mnf-t)",
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Icon name="bookmark" size={18} />
        </button>
      </div>

      <div
        className="flex items-center justify-between gap-2"
        style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(30,74,53,0.08)" }}
      >
        <div className="flex items-center gap-2" style={{ flexWrap: "wrap" }}>
          {workout.timesCompleted > 0 ? (
            <PillTag tone="forest">
              {workout.timesCompleted}× complete
            </PillTag>
          ) : (
            <PillTag tone="fiber">Not yet, sister</PillTag>
          )}
          <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
            {workout.lastCompletedAt
              ? `Last ${relativeTime(workout.lastCompletedAt)}`
              : "Saved for later"}
          </span>
        </div>
        <Link href="/move">
          <Btn variant="primary" size="sm">
            <span>Begin again</span>
            <Icon name="arrow_forward" size={14} />
          </Btn>
        </Link>
      </div>
    </GlassCard>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const day = 1000 * 60 * 60 * 24;
  const days = Math.floor(diff / day);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  return `${Math.floor(days / 30)} mo ago`;
}
